import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Absence, YearlyVacationBalance } from '~/data/absences';
import { useApplicationConfig } from '~/data/applicationConfig';
import { useCompanyData } from '~/data/company/useCompanyData';
import { Enum, EnumListKey, useAllEnums } from '~/data/enums';
import {
  browserLogError,
  filterActiveEnums,
  getEnumName,
  getEnumObjects,
  getEnumObjectsByExtCodes,
} from '~/utils/helper';

export enum BalancePoolCategory {
  VacationBalance = 'default',
  ExtraVacationBalance = 'extra',
  VacationBankBalance = 'bank',
  YearlyVacationBalance = 'yearly',
}

enum VacationBankLabelKeys {
  VacationBank1 = 'VACATION_BANK_1',
  VacationBank2 = 'VACATION_BANK_2',
  VacationBank3 = 'VACATION_BANK_3',
}

enum VacationBalanceLabelKeys {
  ExtraBalance = 'EXTRA_BALANCE',
}

export type BalancePool = {
  name: string;
  balance: number;
  category: BalancePoolCategory;
  reasons?: Enum[];
};

export type BalancePoolConfigs = {
  negativeHolidayBalanceEnabled: boolean;
  vacationBankEnabled: boolean;
  yearlyVacationBalanceEnabled: boolean;
};

export type AvailableBalances = {
  totalBalance: number;
  balancePools: BalancePool[];
  balancePoolConfigs: BalancePoolConfigs;
};

type PropsType = {
  absence?: Absence;
};

type UseVacationBalancesOutput = {
  availableBalances: AvailableBalances;
  isLoading: boolean;
  failedToLoad: boolean;
  balancePools: BalancePool[];
};

export const useVacationBalances = (
  props: PropsType,
): UseVacationBalancesOutput => {
  const { absence } = props;

  const { t } = useTranslation();

  const {
    applicationConfig,
    isLoading: isLoadingApplicationConfig,
    failedToLoad: failedToLoadApplicationConfig,
    hasData: hasApplicationConfig,
  } = useApplicationConfig();
  const {
    companyData,
    isLoading: isLoadingCompanyData,
    failedToLoad: failedToLoadCompanyData,
    hasData: hasCompanyData,
  } = useCompanyData();
  const { getEnums } = useAllEnums();

  const isLoading = isLoadingApplicationConfig || isLoadingCompanyData;
  const failedToLoad = failedToLoadApplicationConfig || failedToLoadCompanyData;

  const { vacationBankConfigs, activeShortAbsenceReasons } = useMemo(() => {
    const activeShortAbsenceReasons = filterActiveEnums(
      getEnums(EnumListKey.ABSENCE_SHORT_REASON),
    );

    if (!hasCompanyData) {
      return {
        vacationBankConfigs: [],
        activeShortAbsenceReasons,
      };
    }

    const vacationBankLabelEnums = getEnums(EnumListKey.VACATION_BANK_LABEL);

    const {
      fComVacationBankLeaveTypes1,
      fComVacationBankLeaveTypes2,
      fComVacationBankLeaveTypes3,
    } = companyData?.config?.fComLeaveTypes ?? {};

    const getBankKeys = (labelKey: string, leaveTypes: string[]) => {
      return {
        labelKey,
        reasonCodes: leaveTypes,
      };
    };

    const vacationBankConfigs = [
      getBankKeys(
        VacationBankLabelKeys.VacationBank1,
        fComVacationBankLeaveTypes1,
      ),
      getBankKeys(
        VacationBankLabelKeys.VacationBank2,
        fComVacationBankLeaveTypes2,
      ),
      getBankKeys(
        VacationBankLabelKeys.VacationBank3,
        fComVacationBankLeaveTypes3,
      ),
    ].map((c) => {
      return {
        bankKey: c.labelKey,
        name: getEnumName(vacationBankLabelEnums, c.labelKey).trim(),
        reasons: getEnumObjects(activeShortAbsenceReasons, c.reasonCodes),
      };
    });

    return {
      vacationBankConfigs,
      activeShortAbsenceReasons,
    };
  }, [getEnums, companyData, hasCompanyData]);

  const balancePoolConfigs = useMemo((): BalancePoolConfigs => {
    if (!hasApplicationConfig) {
      return {
        negativeHolidayBalanceEnabled: false,
        vacationBankEnabled: false,
        yearlyVacationBalanceEnabled: false,
      };
    }

    const {
      fComEnableVacationBank,
      fComEnableYearlyVacationBalance,
      fComEnableNegativeHolidayBalance,
    } = applicationConfig.fComConfig.absConfig;

    return {
      negativeHolidayBalanceEnabled: fComEnableNegativeHolidayBalance,
      vacationBankEnabled: fComEnableVacationBank,
      yearlyVacationBalanceEnabled: fComEnableYearlyVacationBalance,
    };
  }, [applicationConfig, hasApplicationConfig]);

  const availableBalances: AvailableBalances = useMemo(() => {
    if (!absence) {
      return {
        totalBalance: 0,
        balancePools: [],
        balancePoolConfigs,
      };
    }

    const { vacationBalance: balances } = absence;
    const { vacationBankEnabled, yearlyVacationBalanceEnabled } =
      balancePoolConfigs;

    const getAvailableBalance = (
      balance: number,
      projDed: { fProjectedApproved: number; fProjectedUnapproved: number },
    ): number => {
      return !!balance
        ? balance - projDed.fProjectedApproved - projDed.fProjectedUnapproved
        : 0;
    };

    const getAvailableYearlyBalance = (
      yearlyBalance: YearlyVacationBalance,
    ): number => {
      const { fHolidayBalance, fHolidayApproved, fHolidayUnapproved } =
        yearlyBalance;

      return !!fHolidayBalance
        ? fHolidayBalance - fHolidayApproved - fHolidayUnapproved
        : 0;
    };

    const vacationBalance: BalancePool = {
      name: t('laVacBal'),
      balance: getAvailableBalance(
        balances.fVacationBalance,
        balances.fVacationBalanceProjDed,
      ),
      category: BalancePoolCategory.VacationBalance,
    };

    const vacationBalanceLabelEnums = getEnums(
      EnumListKey.VACATION_BALANCE_LABEL,
    );
    const extraVacationBalance: BalancePool = {
      name: getEnumName(
        vacationBalanceLabelEnums,
        VacationBalanceLabelKeys.ExtraBalance,
      ).trim(),
      balance: getAvailableBalance(
        balances.fVacationBalanceExtra ?? 0,
        balances.fVacationBalanceExtraProjDed,
      ),
      category: BalancePoolCategory.ExtraVacationBalance,
    };

    const availableYearlyBalances: BalancePool[] = yearlyVacationBalanceEnabled
      ? balances.fYearlyVacationBalances
          .reduce((balances, balance) => {
            const reasons = getEnumObjectsByExtCodes(
              activeShortAbsenceReasons,
              [balance.fHolidayCode],
            );

            if (!reasons.length) {
              browserLogError(
                `Configuration error - No matching active short absence reason extCode found for yearly vacation code: ${balance.fHolidayCode}`,
              );

              return balances;
            }

            return [
              ...balances,
              {
                name: balance.fHolidayYear.toString(),
                balance: getAvailableYearlyBalance(balance),
                category: BalancePoolCategory.YearlyVacationBalance,
                reasons,
              },
            ];
          }, [] as BalancePool[])
          .sort((a, b) => b.name.localeCompare(a.name))
      : [];

    const getVacationBankBalances = (): BalancePool[] => {
      return [
        {
          bankKey: VacationBankLabelKeys.VacationBank1,
          balance: getAvailableBalance(
            balances.fVacationBalanceBank1,
            balances.fVacationBalanceBank1ProjDed,
          ),
        },
        {
          bankKey: VacationBankLabelKeys.VacationBank2,
          balance: getAvailableBalance(
            balances.fVacationBalanceBank2,
            balances.fVacationBalanceBank2ProjDed,
          ),
        },
        {
          bankKey: VacationBankLabelKeys.VacationBank3,
          balance: getAvailableBalance(
            balances.fVacationBalanceBank3,
            balances.fVacationBalanceBank3ProjDed,
          ),
        },
      ]
        .map(({ bankKey, balance }) => {
          return vacationBankConfigs
            .filter((vbc) => vbc.bankKey === bankKey && !!vbc.name)
            .map((vbc) => {
              const { bankKey, ...bankConfig } = vbc;

              return {
                ...bankConfig,
                balance,
                category: BalancePoolCategory.VacationBankBalance,
              };
            });
        })
        .flat();
    };

    const availableBankBalances: BalancePool[] = vacationBankEnabled
      ? getVacationBankBalances()
      : [extraVacationBalance].filter((b) => !!b.name);

    const availableBalances = [
      vacationBalance,
      ...availableBankBalances,
      ...availableYearlyBalances,
    ];

    const totalAvailableBalance: number = availableBalances.reduce(
      (total: number, pool: BalancePool) => {
        return total + pool.balance;
      },
      0,
    );

    return {
      totalBalance: totalAvailableBalance,
      balancePools: availableBalances,
      balancePoolConfigs,
    };
  }, [
    absence,
    balancePoolConfigs,
    vacationBankConfigs,
    activeShortAbsenceReasons,
    getEnums,
    t,
  ]);

  return {
    availableBalances,
    isLoading,
    failedToLoad,
    balancePools: availableBalances.balancePools,
  };
};
